import { AdminSortDirection } from '../../__generated__/globalTypes';
import CopyButton from '../../components/CopyButton';
import Table from '../../components/Table';

import { formatWorkItemTimestamp, formatWorkItemTimestampFull, type IWorkItemSort } from './workItemsView';

import type { AdminWorkItemSortField } from '../../__generated__/globalTypes';
import type { TableSortDirection } from '../../components/Table';
import type * as React from 'react';

/** Shared look for cell triggers — text that opens a modal. */
export const CELL_TRIGGER_CLASS =
  'block w-full cursor-pointer text-left hover:text-fg-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-fg-brand-primary';

interface IIdCellProps {
  id: string;
}

/**
 * The row's own id, clipped to the column with a copy button beside it — the
 * operator's copy is the value a psql query needs, so both the button and
 * `select-all` must yield the full uuid rather than the visible prefix.
 */
export const IdCell = ({ id }: IIdCellProps) => (
  <Table.Cell mono className="text-xs" title={id}>
    <div className="flex items-center gap-1">
      <span className="select-all truncate">{id}</span>
      <CopyButton value={id} label="Copy id" successMessage="Id copied." />
    </div>
  </Table.Cell>
);

interface IDateCellProps {
  iso: string | null;
}

/** A compact Pacific timestamp, with the full one on hover; null dashes. */
export const DateCell = ({ iso }: IDateCellProps) => {
  if (iso === null) {
    return <Table.Cell className="text-fg-quaternary">—</Table.Cell>;
  }
  return (
    <Table.Cell mono className="whitespace-nowrap text-xs" title={formatWorkItemTimestampFull(iso)}>
      {formatWorkItemTimestamp(iso)}
    </Table.Cell>
  );
};

interface IEmailCellProps {
  email: string | null;
  /** What a null email means in this column, e.g. the assistant holding the work. */
  nullLabel?: string;
}

/**
 * A member's email, clipped to the column with the full address on hover. A
 * null email dashes, unless the column has a `nullLabel` — a column where null
 * means something specific rather than missing data says so instead.
 */
export const EmailCell = ({ email, nullLabel }: IEmailCellProps) => {
  if (email === null) {
    return nullLabel === undefined ? (
      <Table.Cell className="text-fg-quaternary">—</Table.Cell>
    ) : (
      <Table.Cell className="text-fg-tertiary text-xs">{nullLabel}</Table.Cell>
    );
  }
  return (
    <Table.Cell mono className="truncate text-xs" title={email}>
      {email}
    </Table.Cell>
  );
};

interface IExpandableHeaderCellProps {
  children: React.ReactNode;
}

/** Column title with the affordance hint for cells that open a modal. */
export const ExpandableHeaderCell = ({ children }: IExpandableHeaderCellProps) => (
  <Table.HeaderCell>
    {children} <span className="text-fg-quaternary font-normal normal-case tracking-normal">· click to expand</span>
  </Table.HeaderCell>
);

interface ISortableHeaderCellProps {
  field: AdminWorkItemSortField;
  sort: IWorkItemSort;
  onSort: (field: AdminWorkItemSortField) => void;
  children: React.ReactNode;
}

/** One date column's title, wired to the table's sort state by its wire enum. */
export const SortableHeaderCell = ({ field, sort, onSort, children }: ISortableHeaderCellProps) => {
  let sortDirection: TableSortDirection | null = null;
  if (sort.field === field) {
    sortDirection = sort.direction === AdminSortDirection.Asc ? 'asc' : 'desc';
  }
  return (
    <Table.SortableHeaderCell sortDirection={sortDirection} onSort={() => onSort(field)}>
      {children}
    </Table.SortableHeaderCell>
  );
};
